import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  useTheme
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import Toast from '../../components/Toast';
import SEO from '../../components/SEO';
import authService from '../../services/authService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });
  const theme = useTheme();

  const handleChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setToast({
        open: true,
        message: 'Please enter your email address',
        severity: 'error'
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await authService.forgotPassword(email);
      
      if (response.success) {
        setSuccess(true);
        setToast({
          open: true,
          message: 'Password reset link sent! Please check your email.',
          severity: 'success'
        });
      }
    } catch (err) {
      console.error('Password reset error:', err);
      
      // Extract error details
      let errorMessage = 'Failed to send password reset email. Please try again.';
      let errorCode = '';
      
      // Handle parsed JSON error
      if (typeof err.message === 'string' && err.message.startsWith('{')) {
        try {
          const parsedError = JSON.parse(err.message);
          errorCode = parsedError.code;
          errorMessage = parsedError.message;
        } catch (e) {
          console.log('Failed to parse error JSON');
        }
      } else if (err.code && err.message) {
        errorCode = err.code;
        errorMessage = err.message;
      }
      
      // Show appropriate error message
      if (errorCode === 'EMAIL_NOT_FOUND') {
        setToast({
          open: true,
          message: 'No account found with this email address.',
          severity: 'error'
        });
      } else {
        setToast({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Forgot Password | Gravity Point Media" 
        description="Reset your Gravity Point Media account password" 
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 6 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <Box textAlign="center" mb={4}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent'
                }}
              >
                Forgot Password
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Enter your email address and we'll send you a link to reset your password
              </Typography>
            </Box>

            {success ? (
              <Box textAlign="center" my={4}>
                <Alert severity="success" sx={{ mb: 3 }}>
                  Password reset email sent successfully!
                </Alert>
                <Typography variant="body1" paragraph>
                  We've sent an email to <strong>{email}</strong> with instructions to reset your password.
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  If you don't see the email in your inbox, please check your spam folder.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/login"
                  sx={{ mt: 2 }}
                >
                  Return to Login
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth
                  label="Email address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={handleChange}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0, #1976d2)',
                    },
                    '&:disabled': {
                      background: 'linear-gradient(45deg, #90caf9, #bbdefb)',
                    },
                    mb: 2
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
                
                <Box textAlign="center" mt={3}>
                  <Typography variant="body2" color="text.secondary">
                    <Link
                      to="/login"
                      style={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        }
                      }}
                    >
                      Back to Login
                    </Link>
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
};

export default ForgotPassword;